/**
 * Logger utility for Starfall Courier.
 *
 * Wraps console output behind a log-level guard so debug noise is not emitted
 * in production. Use this instead of bare console.log anywhere in the codebase.
 *
 * Log levels (ascending severity):
 *   DEBUG → INFO → WARN → ERROR
 *
 * In production only WARN and ERROR are emitted.
 *
 * Phase 0 stub — will be extended with file-transport and crash reporting
 * hooks in Phase 5 (Settings / Diagnostics).
 *
 * @module logger
 */

/** Supported log levels. */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// DECISION: Reference `process.env.NODE_ENV` as the exact literal expression
// — Vite replaces it at build time in browser bundles (the `typeof process`
// guard + optional chain defeat that replacement and leave IS_DEV false in
// the browser). Jest runs under Node so `process` exists at runtime.
const IS_DEV = process.env.NODE_ENV !== 'production';

/**
 * Emit a debug-level message. Not emitted in production.
 * @param tag - Short identifier for the calling module, e.g. `'Physics'`.
 * @param message - Human-readable log message.
 * @param data - Optional extra data to include.
 */
export function debug(tag: string, message: string, data?: unknown): void {
  if (!IS_DEV) return;
  // eslint-disable-next-line no-console
  console.log(`[DEBUG][${tag}] ${message}`, ...(data !== undefined ? [data] : []));
}

/**
 * Emit an info-level message. Not emitted in production.
 * @param tag - Short identifier for the calling module.
 * @param message - Human-readable log message.
 * @param data - Optional extra data to include.
 */
export function info(tag: string, message: string, data?: unknown): void {
  if (!IS_DEV) return;
  // eslint-disable-next-line no-console
  console.info(`[INFO][${tag}] ${message}`, ...(data !== undefined ? [data] : []));
}

/**
 * Emit a warning. Shown in dev and production.
 * @param tag - Short identifier for the calling module.
 * @param message - Human-readable warning message.
 * @param data - Optional extra data to include.
 */
export function warn(tag: string, message: string, data?: unknown): void {
  console.warn(`[WARN][${tag}] ${message}`, ...(data !== undefined ? [data] : []));
}

/**
 * Emit an error. Always shown.
 * @param tag - Short identifier for the calling module.
 * @param message - Human-readable error message.
 * @param error - The caught error or extra context.
 */
export function error(tag: string, message: string, error?: unknown): void {
  console.error(`[ERROR][${tag}] ${message}`, ...(error !== undefined ? [error] : []));
}

/** Convenience default export for single-object import: `import Logger from '...'`. */
const Logger = { debug, info, warn, error };
export default Logger;
